import React, { useState } from "react";
import { motion } from "framer-motion";
import shehrozeImage from "../../assets/images/information/shehroze8.jpeg";
import bgMountain from "../../assets/images/hero-image-24.jpg";
import { ArrowUpRight } from "lucide-react";

const childVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: [0.25, 0.1, 0.25, 1] },
  },
};

const ApproachSection = () => {
  const [copied, setCopied] = useState(false);
  const email = "info@broadboyadventures.com";

  const handleCopy = () => {
    navigator.clipboard.writeText(email);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <motion.section
      className="relative w-full bg-black text-white py-32 px-6 md:px-14 overflow-hidden"
    >
      {/* Background */}
      <motion.div className="absolute inset-0 w-full h-full pointer-events-none">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-60"
          style={{ backgroundImage: `url(${bgMountain})` }}
        />
      </motion.div>

      {/* Foreground */}
      <div className="relative max-w-7xl mx-auto">
        {/* Heading */}
        <div className="mb-20">
          <motion.div
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            className="space-y-2 text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-medium"
          >
            <h3 className="text-[60px] sm:text-[120px] md:text-[200px] font-bold text-gray-300/10 leading-none select-none pointer-events-none">
              CONQUER THE IMPOSSIBLE
            </h3>
          </motion.div>
        </div>

        {/* Card Row */}
        <div className="relative grid md:grid-cols-2 gap-12 items-center">
          {/* Left Text */}
          <div className="space-y-10 text-gray-300 md:pr-6 text-lg leading-relaxed">
            <p>
              After conquering 14×8000m peaks, one thing became obvious, adventure isn’t sold in brochures. It’s earned.
            </p>
            <p>
              We guide climbers through the world’s most demanding environments, obsessing over details because every detail matters at altitude.
            </p>
            <p className="text-white font-semibold">
              The BroadBoy way... simple, but never easy.
            </p>

            <motion.div
              className="backdrop-blur-md bg-white/10 border border-white/20 
              rounded-xl p-4 shadow text-sm"
              variants={childVariants}
            >
              ⭐⭐⭐⭐⭐ 4.9 / 5 &nbsp; &nbsp; 32+ happy climbers
            </motion.div>

            <a
              href="https://calendly.com/broadboyadventures/15min"
              target="_blank"
              rel="noopener noreferrer"
              className="group inline-flex w-full items-center justify-between gap-3 rounded-2xl
                bg-white/80 px-4 py-4 text-sm font-medium text-[#162735]
                hover:-translate-y-0.5 hover:bg-white transition"
            >
              <span>Book a 15-Min Call</span>
              <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[#162735] text-white">
                  <ArrowUpRight className="h-4 w-4" />
              </span>
            </a>
          </div>

          {/* Right Profile Card */}
          <motion.div
            whileHover="hover"
            initial="rest"
            animate="rest"
            className="relative rounded-xl overflow-hidden cursor-pointer group"
          >
            <motion.img
              src={shehrozeImage}
              alt="Shehroze Kashif"
              className="w-full h-[450px] object-cover object-center"
              variants={{ rest: { scale: 1 }, hover: { scale: 1.07 } }}
              transition={{ duration: 0.45 }}
            />

            <motion.div
              variants={{
                rest: { opacity: 0, y: 45 },
                hover: {
                  opacity: 1,
                  y: 0,
                  transition: { duration: 0.5, ease: "easeOut" },
                },
              }}
              className="absolute inset-0 bg-gray-600/80 backdrop-blur-sm
              flex flex-col justify-start p-6 md:p-7"
            >
              <div className="mt-3">

                {/* CLICK-TO-COPY EMAIL */}
                <button
                  onClick={handleCopy}
                  className="relative text-base sm:text-lg md:text-xl font-semibold underline decoration-2 underline-offset-4 text-white hover:text-gray-200 transition"
                >
                  {email}

                  {copied && (
                    <span className="absolute left-1/2 -top-6 -translate-x-1/2 
                    bg-white text-black text-xs font-medium px-2 py-1 rounded-md shadow-md">
                      Copied ✔
                    </span>
                  )}
                </button>

                <p className="font-light mt-3 text-4xl md:text-4xl text-gray-200 leading-relaxed">
                  With years spent on the world&apos;s highest peaks, BroadBoy Adventures
                  helps climbers turn ambition into real-world ascent, guided by true
                  experience, not theory.
                </p>

              </div>

              {/* Socials */}
              <div className="flex gap-5 text-sm mt-11">
                {/* X Icon */}
                <a
                  href="https://x.com/Shehrozekashif2"
                  target="_blank"
                  rel="noreferrer"
                  className="hover:opacity-70 "
                >
                  <svg 
                    viewBox="0 0 24 24"
                    width="24"
                    height="24"
                    aria-hidden="true"
                    fill="white"
                  >
                    <g>
                      <path d="M21.742 21.75l-7.563-11.179 7.056-8.321h-2.456l-5.691 6.714-4.54-6.714H2.359l7.29 10.776L2.25 21.75h2.456l6.035-7.118 4.818 7.118h6.191-.008zM7.739 3.818L18.81 20.182h-2.447L5.29 3.818h2.447z"></path>
                    </g>
                  </svg>
                </a>

                {/* Instagram Icon */}
                <a
                  href="https://www.instagram.com/bb.adventures_"
                  target="_blank"
                  rel="noreferrer"
                  className="hover:opacity-70 "
                >
                  <svg
                    width="26"
                    height="26"
                    viewBox="0 0 26 24"
                    fill="none"
                    stroke="white"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <rect x="2" y="2" width="20" height="20" rx="5" ry="4"></rect>
                    <path d="M16 11.37A4 4 0 1 1 12.63 8"></path>
                    <path d="M17.5 6.5h.01"></path>
                  </svg>
                </a>
              </div>

            </motion.div>

            {/* Bottom Name Bar */}
            <div className="border-t border-gray-700 bg-[#162735] px-6 py-4">
              <p className="text-lg font-semibold">Led by Shehroze Kashif</p>
              <p className="text-sm text-gray-400">
                We take trekkers and climbers into the heart of the world’s greatest ranges. Exploring deeper and Climbing higher
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </motion.section>
  );
};

export default ApproachSection;
